/**
 * FlyerCarousel — auto-advancing hero carousel for the two COD Employer Partner flyers.
 *
 * - Autoplay every 6s, pauses on hover / focus.
 * - Prev / next chevrons, keyboard-navigable, dot pagination.
 * - Subtle pulsing accent ring on the active slide to match the site's "life" cues.
 * - Each slide has a bold CTA. Slides opt-out of preloading past the first frame
 *   via `loading="lazy"` on non-active slides.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

const FLYERS = [
  {
    id: "build-workforce",
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_cod-digital/artifacts/z0oohrsv_AB420AA7-0D79-4B30-AE63-E067091E41A9.png",
    alt: "COD flyer — Build Your Workforce. Change Lives. Strengthen Chicago.",
    caption: "Build Your Workforce. Change Lives. Strengthen Chicago.",
    ctaLabel: "Become a COD Employer Partner",
    to: "/onboarding/employer",
  },
  {
    id: "seeking-partners",
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_cod-digital/artifacts/khkva2hd_6865231A-B23D-428C-AB8B-0F7BFE51FFBC.png",
    alt: "COD flyer — Actively Seeking Employer Partners.",
    caption: "Actively Seeking Chicago Employer Partners.",
    ctaLabel: "Post a Job Order",
    to: "/onboarding/employer",
  },
];

const AUTO_MS = 6000;

export function FlyerCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const go = (idx) => setActive(((idx % FLYERS.length) + FLYERS.length) % FLYERS.length);
  const prev = () => go(active - 1);
  const next = () => go(active + 1);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => go(active + 1), AUTO_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };

  return (
    <section
      aria-label="Chicago Opportunity Desk employer flyers"
      className="cod-section bg-slate-50"
      data-testid="flyer-carousel-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-8">
          <p className="cod-eyebrow">Employer Partners</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-3">
            Partner with COD. Hire with confidence.
          </h2>
          <div className="cod-rule-red mt-4"/>
        </div>

        <div
          className="relative"
          role="region"
          aria-roledescription="carousel"
          aria-label="COD employer flyers"
          tabIndex={0}
          onKeyDown={onKey}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          data-testid="flyer-carousel"
        >
          {/* Pulsing accent ring — sits behind the active slide */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-3 rounded-2xl animate-pulse"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(196,17,45,0.18) 0%, rgba(11,44,110,0.10) 45%, transparent 70%)",
              filter: "blur(20px)",
            }}
          />

          <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-2xl bg-white">
            {/* Slide viewport */}
            <div className="relative aspect-square sm:aspect-[4/3] md:aspect-[16/10] bg-white">
              {FLYERS.map((f, i) => (
                <div
                  key={f.id}
                  aria-hidden={i !== active}
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${FLYERS.length} — ${f.caption}`}
                  data-testid={`flyer-slide-${f.id}`}
                  className={`absolute inset-0 transition-opacity duration-700 ease-out ${
                    i === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
                  }`}
                >
                  <img
                    src={f.src}
                    alt={f.alt}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="w-full h-full object-contain bg-white"
                  />
                </div>
              ))}
            </div>

            {/* Caption bar with per-slide CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 sm:px-8 py-5 border-t border-slate-200 bg-white">
              <p className="font-serif text-lg sm:text-xl text-slate-900" data-testid="flyer-caption">
                {FLYERS[active].caption}
              </p>
              <Button
                onClick={() => navigate(FLYERS[active].to)}
                className="cod-bg-red text-white h-11 px-6 rounded-md whitespace-nowrap"
                data-testid="flyer-cta"
              >
                {FLYERS[active].ctaLabel} <ArrowRight size={16} className="ml-2"/>
              </Button>
            </div>

            {/* Prev / Next arrows */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous flyer"
              data-testid="flyer-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/95 hover:bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-800 hover:text-[var(--cod-red)] transition-colors"
            >
              <ChevronLeft size={22}/>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next flyer"
              data-testid="flyer-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/95 hover:bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-800 hover:text-[var(--cod-red)] transition-colors"
            >
              <ChevronRight size={22}/>
            </button>
          </div>

          {/* Dot pagination */}
          <div className="mt-5 flex items-center justify-center gap-2" role="tablist" aria-label="Flyer navigation">
            {FLYERS.map((f, i) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`Show flyer ${i + 1} of ${FLYERS.length}`}
                onClick={() => go(i)}
                data-testid={`flyer-dot-${i}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === active
                    ? "w-8 bg-[var(--cod-red)]"
                    : "w-2.5 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FlyerCarousel;
